import { useState } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTopics } from "@/hooks/useMasterData";

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  /** Controlled open state; falls back to internal state when omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Multi-select for curated topics; the value is a list of topic IDs. Lists
 * active topics plus any selected topics that have since been deactivated,
 * so they can still be removed.
 */
const TopicsMultiSelect = ({ value, onChange, open, onOpenChange }: Props) => {
  const { data: topicsList = [] } = useTopics();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const namesById = new Map(topicsList.map((t) => [t.id, t.name]));
  const nameOf = (id: string) => namesById.get(id) ?? id;
  const activeIds = new Set(topicsList.filter((t) => t.is_active).map((t) => t.id));
  const options = [
    ...topicsList.filter((t) => t.is_active).map((t) => ({ id: t.id, name: t.name, inactive: false })),
    ...value.filter((id) => !activeIds.has(id)).map((id) => ({ id, name: nameOf(id), inactive: true })),
  ];

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className="w-full justify-between font-normal text-muted-foreground hover:text-muted-foreground hover:bg-inherit min-h-[36px] h-auto"
        >
          {value.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {value.map((id) => (
                <Badge key={id} variant="secondary" className="text-xs font-normal">{nameOf(id)}</Badge>
              ))}
            </div>
          ) : (
            "Select topics..."
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
        <Command>
          <CommandInput placeholder="Search topics..." />
          <CommandList>
            <CommandEmpty>No topics found.</CommandEmpty>
            <CommandGroup>
              {options.map((topic) => (
                <CommandItem key={topic.id} value={topic.name} onSelect={() => toggle(topic.id)}>
                  <Checkbox checked={value.includes(topic.id)} className="mr-2" />
                  {topic.name}
                  {topic.inactive && <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default TopicsMultiSelect;
